import { Link } from 'types/api.types';
import axios from 'axios';
import {
  CreateSharedLinkResponse,
  GetSharedLinksResponse,
  SharedLink,
  UpdateSharedLinkResponse,
} from 'types/shares.types';

import {
  getCreateShareLink,
  getSharedLinksLink,
  getSharedAudioPlayAudioLink,
  getDesktopWaveformLink,
} from 'app/store/Shares/selectors';
import {
  buildQueryUrl,
  extractFilenameFromURL,
  isNotFoundError,
  triggerDownload,
} from 'app/utils/utils';
import { addAlertAction } from 'app/store/Alerts/slice';
import { createAsyncThunk } from 'app/store/utils';
import { ApiErrorCodeTypes } from 'types/enums';
import { ActionResponse } from 'app/components/Shares/AudiosShare/types';
import { apiUrls } from 'app/api.config';
import {
  RevokeSharedLinkEffect,
  UpdateSharedLinkEffectData,
} from './effects.types';

export const getSharedAudioListEffect = createAsyncThunk(
  'shares/getLinkList',
  async (
    { guid, next }: { guid: string; next?: Link },
    { dispatch, getState }
  ) => {
    try {
      const state = getState();
      const link = getSharedLinksLink(state);
      if (!link) {
        throw new Error('get shared Link Not Found');
      }
      const url = next?.href || buildQueryUrl(link?.href, { resource: guid });
      const response = await axios.get<GetSharedLinksResponse>(url, {
        withCredentials: true,
      });
      const { items, _links } = response.data.response.share;
      return { items, _links };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const createSharedLinkEffect = createAsyncThunk(
  'shares/createLink',
  async (
    {
      title,
      audioGuid,
      songGuid,
    }: { title: string; audioGuid: string; songGuid: string },
    { dispatch, getState }
  ) => {
    try {
      const createShareLink = getCreateShareLink(getState());
      if (!createShareLink) {
        throw new Error('create shared Link Not Found');
      }
      const response = await axios.request<CreateSharedLinkResponse>({
        url: createShareLink.href,
        method: createShareLink.method,
        withCredentials: true,
        data: {
          resource: audioGuid,
          title,
        },
      });

      dispatch(addAlertAction({ message: 'Link created' }));
      return {
        ...response.data.response.share.item,
        songGuid,
      };
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to create link' }));
      throw e;
    }
  }
);

export const revokeSharedLinkEffect = createAsyncThunk(
  'shares/revokeLink',
  async ({ link }: RevokeSharedLinkEffect, { dispatch }) => {
    try {
      await axios.request({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Link revoked' }));
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to revoke link' }));
      throw e;
    }
  }
);

export const updateSharedLinkEffect = createAsyncThunk(
  'shares/updateLink',
  async ({ link, data }: UpdateSharedLinkEffectData, { dispatch }) => {
    try {
      const response = await axios.request<UpdateSharedLinkResponse>({
        url: link.href,
        method: link.method,
        withCredentials: true,
        data,
      });
      return response.data.response.share.item;
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to update link' }));
      throw e;
    }
  }
);

export const downloadSharedAudioEffect = createAsyncThunk(
  'shares/downloadSharedAudio',
  async ({ link, guid }: { link: Link; guid: string }, { dispatch }) => {
    try {
      if (!link?.href) {
        throw new Error('Download Audio link not found');
      }
      const response = await axios.get<ActionResponse>(
        link.href.replace('{audio.guid}', guid)
      );

      const { action } = response.data.response._links;
      const fileName = extractFilenameFromURL(decodeURI(action.href));
      triggerDownload(action.href, fileName);
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to download file. Please try again' })
      );
      throw e;
    }
  }
);

export const getSharedPlaylistsEffect = createAsyncThunk(
  'shares/getSharedPlaylists',
  async (
    { guid, next }: { guid: string; next?: Link },
    { dispatch, getState }
  ) => {
    try {
      const state = getState();
      const link = getSharedLinksLink(state);
      if (!link) {
        throw new Error('get shared playlist Link Not Found');
      }
      const url = next?.href || buildQueryUrl(link?.href, { resource: guid });
      const response = await axios.get<GetSharedLinksResponse>(url, {
        withCredentials: true,
      });
      const { items, _links } = response.data.response.share;
      return { items, _links };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const createSharedPlaylistLinkEffect = createAsyncThunk<
  SharedLink,
  { title: string; playlistGuid: string }
>(
  'shares/createSharedPlaylistLink',
  async ({ title, playlistGuid }, { dispatch, getState, rejectWithValue }) => {
    try {
      const createShareLink = getCreateShareLink(getState());
      if (!createShareLink) {
        throw new Error('create shared playlist Link Not Found');
      }

      const response = await axios.request<CreateSharedLinkResponse>({
        url: createShareLink.href,
        method: createShareLink.method,
        withCredentials: true,
        data: {
          resource: playlistGuid,
          title,
        },
      });

      dispatch(addAlertAction({ message: 'Playlist Link created' }));
      return response.data.response.share.item;
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED
      ) {
        return rejectWithValue({ errorCode: ApiErrorCodeTypes.LIMIT_REACHED });
      }
      dispatch(addAlertAction({ message: 'Failed to create playlist link' }));
      throw e;
    }
  }
);

export const revokeSharedPlaylistLinkEffect = createAsyncThunk<
  null,
  RevokeSharedLinkEffect
>(
  'shares/revokeSharedPlaylistLink',
  async ({ link }, { dispatch, rejectWithValue }) => {
    try {
      await axios.request({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'Playlist Link revoked' }));
      return null;
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED
      ) {
        return rejectWithValue({ errorCode: ApiErrorCodeTypes.LIMIT_REACHED });
      }
      dispatch(addAlertAction({ message: 'Failed to revoke playlist link' }));
      throw e;
    }
  }
);

export const updateSharedPlaylistLinkEffect = createAsyncThunk<
  SharedLink,
  UpdateSharedLinkEffectData
>(
  'shares/updateSharedPlaylistLink',
  async ({ link, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await axios.request<UpdateSharedLinkResponse>({
        url: link.href,
        method: link.method,
        withCredentials: true,
        data,
      });
      return response.data.response.share.item;
    } catch (e: any) {
      if (
        e?.response?.data?.error?.errorCode === ApiErrorCodeTypes.LIMIT_REACHED
      ) {
        return rejectWithValue({ errorCode: ApiErrorCodeTypes.LIMIT_REACHED });
      }
      dispatch(addAlertAction({ message: 'Failed to update playlist link' }));
      throw e;
    }
  }
);

export const fetchSharedAudioSrcEffect = createAsyncThunk(
  'shares/fetchSharedAudioSrc',
  async (
    data: { audioGuid: string; playlistAudioGuid?: string },
    { getState, dispatch }
  ) => {
    const { audioGuid } = data;

    const audioPlayAudioLink = getSharedAudioPlayAudioLink(getState());
    if (!audioPlayAudioLink?.href) {
      throw new Error('Play Audio link not found');
    }

    const response: {
      audioSrc: string;
      waveformSrc?: string;
    } = {
      audioSrc: '',
    };

    try {
      const audioPlaySrcResponse = await axios.get<ActionResponse>(
        audioPlayAudioLink.href.replace('{audio.guid}', audioGuid)
      );
      response.audioSrc = audioPlaySrcResponse.data.response._links.action.href;
    } catch (e) {
      if (isNotFoundError(e)) {
        dispatch(
          addAlertAction({ message: 'This track is no longer available' })
        );
      } else {
        dispatch(addAlertAction({ message: 'Failed to to load audio' }));
      }
      throw e;
    }

    try {
      const waveSrcLink = getDesktopWaveformLink(getState());
      const waveSrcResponse = waveSrcLink
        ? await axios.get<ActionResponse>(
            waveSrcLink.href.replace('{audio.guid}', audioGuid)
          )
        : null;
      response.waveformSrc = waveSrcResponse?.data.response._links.action.href;
    } catch (e) {
      throw new Error('Failed to get waveform src');
    }

    return response;
  }
);

export const fetchSharedResourceEffect = createAsyncThunk(
  'shares/fetchSharedResource',
  async ({
    shareToken,
    audioGuid,
    resourceType = 'lyrics',
  }: {
    shareToken?: string;
    audioGuid?: string;
    resourceType?: 'lyrics' | 'contributions';
  }) => {
    if (!shareToken) {
      throw new Error('ShareToken is missing');
    }

    const href = `${apiUrls.shares}/token/{shareToken}/resource/{resourceType}?audio=${audioGuid}`
      .replace('{shareToken}', shareToken)
      .replace('{resourceType}', resourceType);

    try {
      const response = await axios.get(href);
      return response.data.response;
    } catch (e) {
      throw new Error(`Failed to get shared ${resourceType}`);
    }
  }
);
